/**
 * Ad Performance Tracker
 *
 * Tracks ad-related metrics using Firebase Analytics for monitoring ad performance
 * IMPORTANT: Only tracks impressions and viewability - NEVER track clicks (violates AdSense TOS)
 */

import { getAnalytics, logEvent, setUserProperties } from 'firebase/analytics';

export interface AdImpressionData {
  adSlot: string;
  adFormat: 'sidebar' | 'in-content' | 'auto';
  pageUrl: string;
  timestamp: number;
  isVisible: boolean;
}

export interface AdViewabilityData {
  adSlot: string;
  viewDuration: number; // in seconds
  viewPercentage: number; // 0-100
  pageUrl: string;
}

export class AdPerformanceTracker {
  private static analytics: ReturnType<typeof getAnalytics> | null = null;
  private static impressionCount = 0;
  private static viewabilityTracking = new Map<string, { startTime: number; observer: IntersectionObserver | null }>();

  /**
   * Initialize the tracker with Firebase Analytics
   */
  static initialize() {
    try {
      if (typeof window !== 'undefined') {
        this.analytics = getAnalytics();
      }
    } catch (error) {
    }
  }

  /**
   * Track when an ad impression occurs (ad loads)
   * NOTE: This tracks when the ad SLOT loads, not clicks
   */
  static trackAdImpression(data: AdImpressionData) {
    if (!this.analytics) return;

    try {
      this.impressionCount++;

      logEvent(this.analytics, 'ad_impression', {
        ad_slot: data.adSlot,
        ad_format: data.adFormat,
        page_url: data.pageUrl,
        is_visible: data.isVisible,
        session_impressions: this.impressionCount,
      });

    } catch (error) {
    }
  }

  /**
   * Track ad viewability (how long ad was visible to user)
   * Uses Intersection Observer API to detect when ad is in viewport
   */
  static trackAdViewability(adElement: HTMLElement, adSlot: string, adFormat: 'sidebar' | 'in-content' | 'auto') {
    if (!this.analytics || typeof window === 'undefined') return;

    try {
      // Create intersection observer for this ad
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Ad entered viewport - start tracking
              const trackingData = this.viewabilityTracking.get(adSlot);
              if (!trackingData || !trackingData.startTime) {
                this.viewabilityTracking.set(adSlot, {
                  startTime: Date.now(),
                  observer,
                });
              }
            } else {
              // Ad left viewport - calculate view duration
              const trackingData = this.viewabilityTracking.get(adSlot);
              if (trackingData && trackingData.startTime) {
                const viewDuration = (Date.now() - trackingData.startTime) / 1000; // Convert to seconds

                // Only track if viewed for at least 1 second
                if (viewDuration >= 1) {
                  this.logViewability({
                    adSlot,
                    viewDuration,
                    viewPercentage: entry.intersectionRatio * 100,
                    pageUrl: window.location.pathname,
                  }, adFormat);
                }

                // Reset tracking
                this.viewabilityTracking.set(adSlot, {
                  startTime: 0,
                  observer: trackingData.observer,
                });
              }
            }
          });
        },
        {
          threshold: [0, 0.25, 0.5, 0.75, 1.0], // Track at different visibility levels
          rootMargin: '0px',
        }
      );

      observer.observe(adElement);
      this.viewabilityTracking.set(adSlot, { startTime: 0, observer });

    } catch (error) {
    }
  }

  /**
   * Stop tracking viewability for a specific ad (cleanup)
   */
  static stopViewabilityTracking(adSlot: string) {
    const trackingData = this.viewabilityTracking.get(adSlot);
    if (trackingData && trackingData.observer) {
      trackingData.observer.disconnect();
      this.viewabilityTracking.delete(adSlot);
    }
  }

  /**
   * Log viewability data to Firebase Analytics
   */
  private static logViewability(data: AdViewabilityData, adFormat: string) {
    if (!this.analytics) return;

    try {
      logEvent(this.analytics, 'ad_viewability', {
        ad_slot: data.adSlot,
        ad_format: adFormat,
        view_duration: Math.round(data.viewDuration * 10) / 10, // Round to 1 decimal
        view_percentage: Math.round(data.viewPercentage),
        page_url: data.pageUrl,
      });

    } catch (error) {
    }
  }

  /**
   * Track which pages have the highest ad performance
   */
  static trackPageAdPerformance(pageUrl: string, adCount: number) {
    if (!this.analytics) return;

    try {
      logEvent(this.analytics, 'page_ad_performance', {
        page_url: pageUrl,
        ad_count: adCount,
        timestamp: Date.now(),
      });
    } catch (error) {
    }
  }

  /**
   * Set user properties related to ad experience
   */
  static setUserAdProperties(properties: {
    hasAdConsent?: boolean;
    isPremium?: boolean;
    adBlockDetected?: boolean;
  }) {
    if (!this.analytics) return;

    try {
      setUserProperties(this.analytics, {
        has_ad_consent: properties.hasAdConsent?.toString(),
        is_premium: properties.isPremium?.toString(),
        ad_block_detected: properties.adBlockDetected?.toString(),
      });

    } catch (error) {
    }
  }

  /**
   * Get current session impression count
   */
  static getImpressionCount(): number {
    return this.impressionCount;
  }

  /**
   * Reset impression count (useful for testing)
   */
  static resetImpressionCount() {
    this.impressionCount = 0;
  }

  /**
   * Cleanup all tracking (call when component unmounts)
   */
  static cleanup() {
    this.viewabilityTracking.forEach((data, adSlot) => {
      this.stopViewabilityTracking(adSlot);
    });
    this.viewabilityTracking.clear();
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  AdPerformanceTracker.initialize();
}
